/**
 * Names close enough to be worth pointing out (T-913).
 *
 * Tags are the one thing users create *while doing something else* — mid-log,
 * in a hurry — so the list accumulates near-duplicates faster than anything
 * else in the app: "snack" and "snacks", "Travel" and "Travelling".
 *
 * **Advisory, always.** "Travel" and "Travelling" might be the same thing or
 * might be a deliberate distinction, and nothing here can tell. This reports
 * pairs; deciding what to do about one is the user's. Nothing in this module
 * merges, renames, blocks a save, or ranks one name above the other — the pair
 * is returned in the order given so the UI cannot read a recommendation into
 * which came first.
 *
 * Deliberately conservative. A notice that fires on unrelated names is noise,
 * and noise on this screen teaches people to ignore the one case where two tags
 * really are the same thing typed twice.
 */

export interface SimilarPair {
  readonly a: string
  readonly b: string
}

/**
 * Casing, spacing and punctuation removed — the differences that are never a
 * deliberate distinction between two tags.
 */
function normalise(name: string): string {
  return name.toLowerCase().replace(/[\s\-_'’.]/g, '')
}

/** Whether one is the other plus a trailing `s` or `es`. */
function isPlural(shorter: string, longer: string): boolean {
  return longer === `${shorter}s` || longer === `${shorter}es`
}

/**
 * Whether two names differ by at most one edit — insertion, deletion or
 * substitution.
 *
 * Bounded at one on purpose. Two edits reaches "cats" from "dogs", and a notice
 * that pairs those is worse than no notice at all.
 */
function withinOneEdit(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) {
    return false
  }
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a]

  let i = 0
  let j = 0
  let edits = 0
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] === longer[j]) {
      i += 1
      j += 1
      continue
    }
    edits += 1
    if (edits > 1) {
      return false
    }
    if (shorter.length === longer.length) {
      i += 1
    }
    j += 1
  }
  return edits + (longer.length - j) <= 1
}

/** Whether two names are close enough to be worth mentioning to their author. */
export function areSimilar(a: string, b: string): boolean {
  const left = normalise(a)
  const right = normalise(b)
  if (left === '' || right === '') {
    return false
  }
  if (left === right) {
    // Identical once punctuation and case are gone — "check-in" and "Check In".
    return true
  }
  if (isPlural(left, right) || isPlural(right, left)) {
    return true
  }
  /*
   * One edit, but only on names long enough for one edit to be a typo rather
   * than the whole word. "in" and "on" are one edit apart and obviously
   * different; "travelling" and "traveling" are the case this is for.
   */
  return left.length >= 4 && right.length >= 4 && withinOneEdit(left, right)
}

/**
 * Every pair worth mentioning, each reported once.
 *
 * Quadratic, which is fine: this runs over a tag list, and a user with enough
 * tags for that to matter has a problem this screen exists to solve.
 */
export function similarNames(names: readonly string[]): readonly SimilarPair[] {
  const pairs: SimilarPair[] = []
  for (let i = 0; i < names.length; i += 1) {
    for (let j = i + 1; j < names.length; j += 1) {
      const a = names[i]
      const b = names[j]
      if (a !== undefined && b !== undefined && areSimilar(a, b)) {
        pairs.push({ a, b })
      }
    }
  }
  return pairs
}
